<template>
    <div class="settings-schedule-page">
        <div class="top-buttons">
            <ui5-button @click="updateScheduleSettings()">
                {{ trans('pages.settings_schedule.save') }}
            </ui5-button>
            <ui5-button @click="$router.push('/settings')">
                {{ trans('pages.settings_schedule.cancel') }}
            </ui5-button>
        </div>

        <spinner-text v-if="loading">
            {{ trans('pages.settings_schedule.loading') }}
        </spinner-text>
        <div v-else>
            <h2>{{ trans('pages.settings_schedule.production_progress') }}</h2>

            <ui5-label>
                {{
                    trans('pages.settings_schedule.production_days_to_display')
                }}
                <i
                    @mouseover="
                        $refs.tooltipProductionProgress.showAt($event.target)
                    "
                    @mouseout="$refs.tooltipProductionProgress.close()"
                    class="fa fa-question-circle"
                />
            </ui5-label>
            <ui5-popover
                ref="tooltipProductionProgress"
                :allow-target-overlap="false"
                placement-type="Bottom"
            >
                <div>
                    {{
                        trans('pages.settings_schedule.production_days_tooltip')
                    }}
                </div>
            </ui5-popover>
            <ui5-input
                type="Text"
                name="scheduleProductionSignageDay"
                :value="scheduleProductionSignageDay"
                @input="scheduleProductionSignageDay = $event.target.value"
            />
            <span
                class="error"
                v-if="
                    !validate(
                        validations.scheduleProductionSignageDay,
                        scheduleProductionSignageDay
                    )
                "
            >
                {{
                    trans(
                        'pages.settings_schedule.error_messages.valid_number_gt_zero'
                    )
                }}
            </span>
            <br />

            <h2>{{ trans('pages.settings_schedule.product_status_delay') }}</h2>

            <ui5-label>
                {{ trans('pages.settings_schedule.hours_exceeding_limit') }}
                <small>{{ trans('pages.settings_schedule.in_hours') }}</small>
                <i
                    @mouseover="
                        $refs.tooltipHoursExceedingLimit.showAt($event.target)
                    "
                    @mouseout="$refs.tooltipHoursExceedingLimit.close()"
                    class="fa fa-question-circle"
                />
            </ui5-label>
            <ui5-popover
                ref="tooltipHoursExceedingLimit"
                :allow-target-overlap="false"
                placement-type="Bottom"
            >
                <div>
                    {{ trans('pages.settings_schedule.hours_exceeding_limit_tooltip') }}
                </div>
            </ui5-popover>
            <text-input
                type="number"
                name="scheduleProductionStatusDelayHours"
                v-model="scheduleProductionStatusDelayHours"
            />
            <span
                class="error"
                v-if="
                    !validate(
                        validations.scheduleProductionStatusDelayHours,
                        scheduleProductionStatusDelayHours
                    )
                "
            >
                {{ trans('pages.settings_schedule.error_messages.valid_number_gt_zero') }}
            </span>
            <br />

            <ui5-label>
                {{ trans('pages.settings_schedule.work_hours_window_1') }}
            </ui5-label>
            <div class="work-hours-window">
                <div class="work-hours-input">
                    <small>{{ trans('pages.settings_schedule.start_time') }}</small>
                    <ui5-input
                        type="Time"
                        name="scheduleWorkHoursStart1"
                        :value="scheduleWorkHoursStart1"
                        @input="scheduleWorkHoursStart1 = $event.target.value"
                    />
                </div>
                <div class="work-hours-input">
                    <small>{{ trans('pages.settings_schedule.end_time') }}</small>
                    <ui5-input
                        type="Time"
                        name="scheduleWorkHoursEnd1"
                        :value="scheduleWorkHoursEnd1"
                        @input="scheduleWorkHoursEnd1 = $event.target.value"
                    />
                </div>
            </div>
            <span
                class="error"
                v-if="isWorkHoursWindow1Invalid"
            >
                {{ trans('pages.settings_schedule.error_messages.valid_time_format') }}
            </span>
            <br />

            <ui5-label>
                {{ trans('pages.settings_schedule.work_hours_window_2') }}
            </ui5-label>
            <div class="work-hours-window">
                <div class="work-hours-input">
                    <small>{{ trans('pages.settings_schedule.start_time') }}</small>
                    <ui5-input
                        type="Time"
                        name="scheduleWorkHoursStart2"
                        :value="scheduleWorkHoursStart2"
                        @input="scheduleWorkHoursStart2 = $event.target.value"
                    />
                </div>
                <div class="work-hours-input">
                    <small>{{ trans('pages.settings_schedule.end_time') }}</small>
                    <ui5-input
                        type="Time"
                        name="scheduleWorkHoursEnd2"
                        :value="scheduleWorkHoursEnd2"
                        @input="scheduleWorkHoursEnd2 = $event.target.value"
                    />
                </div>
            </div>
            <span
                class="error"
                v-if="isWorkHoursWindow2Invalid"
            >
                {{ trans('pages.settings_schedule.error_messages.valid_time_format') }}
            </span>
            <br />

            <h2>{{ trans('pages.settings_schedule.planning') }}</h2>

            <ui5-label>
                {{ trans('pages.settings_schedule.production_efficiency') }}
                <small>
                    {{ trans('pages.settings_schedule.in_percentage') }}
                </small>
                <i
                    class="fa fa-question-circle"
                    @mouseover="
                        $refs.tooltipProductionEfficiency.showAt($event.target)
                    "
                    @mouseout="$refs.tooltipProductionEfficiency.close()"
                />
            </ui5-label>
            <ui5-popover
                ref="tooltipProductionEfficiency"
                :allow-target-overlap="false"
                placement-type="Bottom"
            >
                <div>
                    {{
                        trans(
                            'pages.settings_schedule.production_efficiency_tooltip'
                        )
                    }}
                </div>
            </ui5-popover>

            <ui5-input
                type="Text"
                name="scheduleProductionEfficiency"
                :value="scheduleProductionEfficiency"
                @input="scheduleProductionEfficiency = $event.target.value"
            />
            <span
                class="error"
                v-if="
                    !validate(
                        validations.scheduleProductionEfficiency,
                        scheduleProductionEfficiency
                    )
                "
            >
                {{
                    trans(
                        'pages.settings_schedule.error_messages.valid_percentage'
                    )
                }}
            </span>
            <br />

            <ui5-label>
                {{ trans('pages.settings_schedule.capacity_deviation') }}
                <small>{{ trans('pages.settings_schedule.in_minutes') }}</small>
                <i
                    class="fa fa-question-circle"
                    @mouseover="
                        $refs.tooltipCapacityDeviance.showAt($event.target)
                    "
                    @mouseout="$refs.tooltipCapacityDeviance.close()"
                />
            </ui5-label>
            <ui5-popover
                ref="tooltipCapacityDeviance"
                :allow-target-overlap="false"
                placement-type="Bottom"
            >
                <div>
                    {{
                        trans(
                            'pages.settings_schedule.capacity_deviation_tooltip'
                        )
                    }}
                </div>
            </ui5-popover>
            <ui5-input
                type="Text"
                name="scheduleCapacityDeviation"
                :value="scheduleCapacityDeviation"
                @input="scheduleCapacityDeviation = $event.target.value"
            />
            <span
                class="error"
                v-if="
                    !validate(
                        validations.scheduleCapacityDeviation,
                        scheduleCapacityDeviation
                    )
                "
            >
                {{
                    trans('pages.settings_schedule.error_messages.valid_number')
                }}
            </span>
            <br />

            <ui5-label>
                {{ trans('pages.settings_schedule.balanced_capacity_color') }}
                <small>
                    {{ trans('pages.settings_schedule.color_example') }}
                </small>
                <i
                    class="fa fa-question-circle"
                    @mouseover="
                        $refs.tooltipBalancedCapacity.showAt($event.target)
                    "
                    @mouseout="$refs.tooltipBalancedCapacity.close()"
                />
            </ui5-label>
            <ui5-popover
                ref="tooltipBalancedCapacity"
                :allow-target-overlap="false"
                placement-type="Bottom"
            >
                <div>
                    {{
                        trans(
                            'pages.settings_schedule.balanced_capacity_tooltip'
                        )
                    }}
                </div>
            </ui5-popover>
            <ui5-input
                type="Text"
                name="scheduleCapacityBalanceColor"
                :value="scheduleCapacityBalanceColor"
                @input="scheduleCapacityBalanceColor = $event.target.value"
            />
            <span
                class="error"
                v-if="
                    !validate(
                        validations.scheduleCapacityBalanceColor,
                        scheduleCapacityBalanceColor
                    )
                "
            >
                {{
                    trans('pages.settings_schedule.error_messages.valid_color')
                }}
            </span>
            <br />

            <ui5-label>
                {{ trans('pages.settings_schedule.excess_capacity_color') }}
                <small>
                    {{ trans('pages.settings_schedule.color_example') }}
                </small>
                <i
                    class="fa fa-question-circle"
                    @mouseover="
                        $refs.tooltipExcessCapacity.showAt($event.target)
                    "
                    @mouseout="$refs.tooltipExcessCapacity.close()"
                />
            </ui5-label>
            <ui5-popover
                ref="tooltipExcessCapacity"
                :allow-target-overlap="false"
                placement-type="Bottom"
            >
                <div>
                    {{
                        trans('pages.settings_schedule.excess_capacity_tooltip')
                    }}
                </div>
            </ui5-popover>
            <ui5-input
                type="Text"
                name="scheduleCapacitySurplusColor"
                :value="scheduleCapacitySurplusColor"
                @input="scheduleCapacitySurplusColor = $event.target.value"
            />
            <span
                class="error"
                v-if="
                    !validate(
                        validations.scheduleCapacitySurplusColor,
                        scheduleCapacitySurplusColor
                    )
                "
            >
                {{
                    trans('pages.settings_schedule.error_messages.valid_color')
                }}
            </span>
            <br />

            <ui5-label>
                {{ trans('pages.settings_schedule.capacity_shortage_color') }}
                <small>
                    {{ trans('pages.settings_schedule.color_example') }}
                </small>
                <i
                    class="fa fa-question-circle"
                    @mouseover="
                        $refs.tooltipLackOfCapacity.showAt($event.target)
                    "
                    @mouseout="$refs.tooltipLackOfCapacity.close()"
                />
            </ui5-label>
            <ui5-popover
                ref="tooltipLackOfCapacity"
                :allow-target-overlap="false"
                placement-type="Bottom"
            >
                <div>
                    {{
                        trans(
                            'pages.settings_schedule.capacity_shortage_tooltip'
                        )
                    }}
                </div>
            </ui5-popover>
            <ui5-input
                type="Text"
                name="scheduleCapacityShortageColor"
                :value="scheduleCapacityShortageColor"
                @input="scheduleCapacityShortageColor = $event.target.value"
            />
            <span
                class="error"
                v-if="
                    !validate(
                        validations.scheduleCapacityShortageColor,
                        scheduleCapacityShortageColor
                    )
                "
            >
                {{
                    trans('pages.settings_schedule.error_messages.valid_color')
                }}
            </span>
            <br />

            <h2>
                {{
                    trans('pages.settings_schedule.production_date_calculation')
                }}
            </h2>

            <ui5-label>
                {{ trans('pages.settings_schedule.buffer_days_delivery') }}
                <small>{{ trans('pages.settings_schedule.in_days') }}</small>
                <i
                    class="fa fa-question-circle"
                    @mouseover="
                        $refs.tooltipDeliveryBufferDays.showAt($event.target)
                    "
                    @mouseout="$refs.tooltipDeliveryBufferDays.close()"
                />
            </ui5-label>
            <ui5-popover
                ref="tooltipDeliveryBufferDays"
                :allow-target-overlap="false"
                placement-type="Bottom"
            >
                <div>
                    {{
                        trans(
                            'pages.settings_schedule.buffer_days_delivery_tooltip'
                        )
                    }}
                </div>
            </ui5-popover>
            <text-input
                type="number"
                name="scheduleProductionTimeBuffer"
                v-model="scheduleProductionTimeBuffer"
            />
            <span
                class="error"
                v-if="
                    !validate(
                        validations.scheduleProductionTimeBuffer,
                        scheduleProductionTimeBuffer
                    )
                "
            >
                {{
                    trans('pages.settings_schedule.error_messages.valid_number')
                }}
            </span>
            <br />

            <ui5-label>
                {{ trans('pages.settings_schedule.production_days_mm') }}
                <small>{{ trans('pages.settings_schedule.in_days') }}</small>
                <i
                    class="fa fa-question-circle"
                    @mouseover="
                        $refs.tooltipProductionMMDays.showAt($event.target)
                    "
                    @mouseout="$refs.tooltipProductionMMDays.close()"
                />
            </ui5-label>
            <ui5-popover
                ref="tooltipProductionMMDays"
                :allow-target-overlap="false"
                placement-type="Bottom"
            >
                <div>
                    {{
                        trans(
                            'pages.settings_schedule.production_days_mm_tooltip'
                        )
                    }}
                </div>
            </ui5-popover>
            <text-input
                type="number"
                name="scheduleProductionTimeMM"
                v-model="scheduleProductionTimeMM"
            />
            <span
                class="error"
                v-if="
                    !validate(
                        validations.scheduleProductionTimeMM,
                        scheduleProductionTimeMM
                    )
                "
            >
                {{
                    trans('pages.settings_schedule.error_messages.valid_number')
                }}
            </span>
            <br />

            <ui5-label>
                {{ trans('pages.settings_schedule.buffer_days_mm') }}
                <small>{{ trans('pages.settings_schedule.in_days') }}</small>
                <i
                    class="fa fa-question-circle"
                    @mouseover="$refs.tooltipMMBufferDays.showAt($event.target)"
                    @mouseout="$refs.tooltipMMBufferDays.close()"
                />
            </ui5-label>
            <ui5-popover
                ref="tooltipMMBufferDays"
                :allow-target-overlap="false"
                placement-type="Bottom"
            >
                <div>
                    {{
                        trans('pages.settings_schedule.buffer_days_mm_tooltip')
                    }}
                </div>
            </ui5-popover>
            <text-input
                type="number"
                name="scheduleProductionTimeBufferMM"
                v-model="scheduleProductionTimeBufferMM"
            />
            <span
                class="error"
                v-show="
                    !validate(
                        validations.scheduleProductionTimeBufferMM,
                        scheduleProductionTimeBufferMM
                    )
                "
            >
                {{
                    trans('pages.settings_schedule.error_messages.valid_number')
                }}
            </span>
            <br />

            <h2>{{ trans('pages.settings_schedule.other') }}</h2>
            <ui5-label>
                {{ trans('pages.settings_schedule.max_duration_paintlist') }}
                <small>{{ trans('pages.settings_schedule.in_days') }}</small>
            </ui5-label>
            <text-input
                type="number"
                name="schedulePaintlistTimeMax"
                v-model="schedulePaintlistTimeMax"
            />
            <span
                class="error"
                v-show="
                    !validate(
                        validations.schedulePaintlistTimeMax,
                        schedulePaintlistTimeMax
                    )
                "
            >
                {{
                    trans('pages.settings_schedule.error_messages.valid_number')
                }}
            </span>
            <br />

            <ui5-label>
                {{ trans('pages.settings_schedule.kg_powder_per_area') }}
            </ui5-label>
            <text-input
                type="number"
                name="scheduleKgPowderPerArea"
                v-model="scheduleKgPowderPerArea"
            />
            <span
                class="error"
                v-show="
                    !validate(
                        validations.scheduleKgPowderPerArea,
                        scheduleKgPowderPerArea
                    )
                "
            >
                {{
                    trans(
                        'pages.settings_schedule.error_messages.valid_numeric'
                    )
                }}
            </span>
            <br />

            <ui5-label>
                {{ trans('pages.settings_schedule.margin_paintlist') }}
                <small>{{ trans('pages.settings_schedule.in_mm') }}</small>
            </ui5-label>
            <text-input
                type="number"
                name="scheduleMarginPaintlist"
                v-model="scheduleMarginPaintlist"
            />
            <span
                class="error"
                v-show="
                    !validate(
                        validations.scheduleMarginPaintlist,
                        scheduleMarginPaintlist
                    )
                "
            >
                {{
                    trans(
                        'pages.settings_schedule.error_messages.valid_numeric'
                    )
                }}
            </span>
            <br />

            <ui5-label>
                {{
                    trans(
                        'pages.settings_schedule.max_duration_future_paintlist'
                    )
                }}
                <small>{{ trans('pages.settings_schedule.in_days') }}</small>
            </ui5-label>
            <text-input
                type="number"
                name="scheduleFuturePaintlistform"
                v-model="scheduleFuturePaintlistform"
            />
            <span
                class="error"
                v-show="
                    !validate(
                        validations.scheduleFuturePaintlistform,
                        scheduleFuturePaintlistform
                    )
                "
            >
                {{
                    trans('pages.settings_schedule.error_messages.valid_number')
                }}
            </span>
            <br />

            <ui5-label>
                {{ trans('pages.settings_schedule.auto_create_rides') }}
                <small>{{ trans('pages.settings_schedule.in_months') }}</small>
            </ui5-label>
            <text-input
                type="number"
                name="deliveryrouteSchedulingMonthsInAdvance"
                v-model="deliveryrouteSchedulingMonthsInAdvance"
            />
            <span
                class="error"
                v-show="
                    !validate(
                        validations.deliveryrouteSchedulingMonthsInAdvance,
                        deliveryrouteSchedulingMonthsInAdvance
                    )
                "
            >
                {{
                    trans(
                        'pages.settings_schedule.error_messages.valid_number_ge_zero'
                    )
                }}
            </span>
            <br />

            <ui5-label>
                {{ trans('pages.settings_schedule.pleating_length_threshold') }}
                <small>{{ trans('pages.settings_schedule.in_mm') }}</small>
                <i
                    class="fa fa-question-circle"
                    @mouseover="$refs.tooltipPleatingLength.showAt($event.target)"
                    @mouseout="$refs.tooltipPleatingLength.close()"
                />
            </ui5-label>
            <ui5-popover
                ref="tooltipPleatingLength"
                :allow-target-overlap="false"
                placement-type="Bottom"
            >
                <div>{{ trans('pages.settings_schedule.pleating_length_threshold_tooltip') }}</div>
            </ui5-popover>
            <text-input
                type="number"
                name="pleatingLengthThreshold"
                v-model="pleatingLengthThreshold"
            />
            <span
                class="error"
                v-show="!validate(validations.pleatingLengthThreshold, pleatingLengthThreshold)"
            >
                {{ trans('pages.settings_schedule.error_messages.valid_optional_integer') }}
            </span>
            <br />
        </div>
    </div>
</template>
<script>
import SpinnerText from '../components/loader/SpinnerText';
import settingsApi from '../api/settings';
import { showInfoMessage, showErrorMessage } from '../dialogs';
import localization from '../mixins/localization';
import TextInput from '../components/TextInput';

export default {
    components: { SpinnerText, TextInput },
    mixins: [localization],
    computed: {
        all() {
            return {
                scheduleProductionSignageDay: this.scheduleProductionSignageDay,
                scheduleProductionEfficiency: this.scheduleProductionEfficiency,
                scheduleCapacityDeviation: this.scheduleCapacityDeviation,
                scheduleCapacityBalanceColor: this.scheduleCapacityBalanceColor,
                scheduleCapacitySurplusColor: this.scheduleCapacitySurplusColor,
                scheduleCapacityShortageColor:
                    this.scheduleCapacityShortageColor,
                scheduleProductionTimeBuffer: this.scheduleProductionTimeBuffer,
                scheduleProductionTimeMM: this.scheduleProductionTimeMM,
                scheduleProductionTimeBufferMM:
                    this.scheduleProductionTimeBufferMM,
                schedulePaintlistTimeMax: this.schedulePaintlistTimeMax,
                scheduleKgPowderPerArea: this.scheduleKgPowderPerArea,
                scheduleMarginPaintlist: this.scheduleMarginPaintlist,
                scheduleFuturePaintlistform: this.scheduleFuturePaintlistform,
                deliveryrouteSchedulingMonthsInAdvance:
                    this.deliveryrouteSchedulingMonthsInAdvance,
                pleatingLengthThreshold: this.pleatingLengthThreshold,
                scheduleProductionStatusDelayHours: this.scheduleProductionStatusDelayHours,
                scheduleWorkHoursStart1: this.scheduleWorkHoursStart1,
                scheduleWorkHoursEnd1: this.scheduleWorkHoursEnd1,
                scheduleWorkHoursStart2: this.scheduleWorkHoursStart2,
                scheduleWorkHoursEnd2: this.scheduleWorkHoursEnd2,
            };
        },
        isWorkHoursWindow1Invalid() {
            return !this.validate(
                this.validations.scheduleWorkHoursStart1,
                this.scheduleWorkHoursStart1
            ) ||
            !this.validate(
                this.validations.scheduleWorkHoursEnd1,
                this.scheduleWorkHoursEnd1
            );
        },
        isWorkHoursWindow2Invalid() {
            return !this.validate(
                this.validations.scheduleWorkHoursStart2,
                this.scheduleWorkHoursStart2
            ) ||
            !this.validate(
                this.validations.scheduleWorkHoursEnd2,
                this.scheduleWorkHoursEnd2
            );
        },
    },
    data() {
        return {
            loading: true,
            scheduleProductionSignageDay: '',
            scheduleProductionEfficiency: '',
            scheduleCapacityDeviation: '',
            scheduleCapacityBalanceColor: '',
            scheduleCapacitySurplusColor: '',
            scheduleCapacityShortageColor: '',
            scheduleProductionTimeBuffer: '',
            scheduleProductionTimeMM: '',
            scheduleProductionTimeBufferMM: '',
            schedulePaintlistTimeMax: '',
            scheduleKgPowderPerArea: '',
            scheduleMarginPaintlist: '',
            scheduleFuturePaintlistform: '',
            deliveryrouteSchedulingMonthsInAdvance: '',
            pleatingLengthThreshold: '',
            scheduleProductionStatusDelayHours: '',
            scheduleWorkHoursStart1: '',
            scheduleWorkHoursEnd1: '',
            scheduleWorkHoursStart2: '',
            scheduleWorkHoursEnd2: '',
            validations: {
                scheduleProductionSignageDay: 'validatePositiveNumeric',
                scheduleCapacityDeviation: 'validateInteger',
                scheduleProductionTimeBuffer: 'validateInteger',
                scheduleProductionTimeMM: 'validateInteger',
                scheduleProductionTimeBufferMM: 'validateInteger',
                schedulePaintlistTimeMax: 'validateInteger',
                scheduleFuturePaintlistform: 'validateInteger',
                deliveryrouteSchedulingMonthsInAdvance:
                    'validatePositiveInteger',
                scheduleProductionEfficiency: 'validateInteger0to100',
                scheduleCapacityBalanceColor: 'validateColor',
                scheduleCapacitySurplusColor: 'validateColor',
                scheduleCapacityShortageColor: 'validateColor',
                scheduleKgPowderPerArea: 'validateNumeric',
                scheduleMarginPaintlist: 'validateNumeric',
                pleatingLengthThreshold: 'validateOptionalPositiveInteger',
                scheduleProductionStatusDelayHours: 'validatePositiveInteger',
                scheduleWorkHoursStart1: 'validateTimeFormat',
                scheduleWorkHoursEnd1: 'validateTimeFormat',
                scheduleWorkHoursStart2: 'validateTimeFormat',
                scheduleWorkHoursEnd2: 'validateTimeFormat',
            },
        };
    },
    methods: {
        validate(validator, value) {
            return this[validator](value);
        },
        validateInteger(value) {
            return value && value.match(/^[0-9]+$/);
        },
        validatePositiveInteger(value) {
            return this.validateInteger(value) && value > 0;
        },
        validateInteger0to100(value) {
            return this.validateInteger(value) && value >= 0 && value <= 100;
        },
        validateColor(value) {
            return value && value.match(/^#[0-9a-fA-F]{3,6}$/);
        },
        validateNumeric(value) {
            return value && !isNaN(value);
        },
        validatePositiveNumeric(value) {
            return this.validateNumeric(value) && value > 0;
        },
        validateOptionalPositiveInteger(value) {
            return value === '' || value === null || (value && value.match(/^[1-9][0-9]*$/));
        },
        validateTimeFormat(value) {
            return value && value.match(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/);
        },
        validateWorkTimeWindows() {
            const start1 = this.scheduleWorkHoursStart1;
            const end1 = this.scheduleWorkHoursEnd1;
            const start2 = this.scheduleWorkHoursStart2;
            const end2 = this.scheduleWorkHoursEnd2;

            if (!this.validateTimeFormat(start1) || !this.validateTimeFormat(end1)) {
                return { valid: false, message: this.trans('pages.settings_schedule.error_messages.valid_time_format') };
            }

            if (!this.validateTimeFormat(start2) || !this.validateTimeFormat(end2)) {
                return { valid: false, message: this.trans('pages.settings_schedule.error_messages.valid_time_format') };
            }

            const timeToMinutes = (timeStr) => {
                const [hours, minutes] = timeStr.split(':').map(Number);
                return hours * 60 + minutes;
            };

            const start1Min = timeToMinutes(start1);
            const end1Min = timeToMinutes(end1);
            const start2Min = timeToMinutes(start2);
            const end2Min = timeToMinutes(end2);

            if (start1Min >= end1Min) {
                return {
                    valid: false,
                    message: this.trans('pages.settings_schedule.error_messages.window_1_reversed'),
                };
            }

            if (start2Min >= end2Min) {
                return {
                    valid: false,
                    message: this.trans('pages.settings_schedule.error_messages.window_2_reversed'),
                };
            }

            if (end1Min >= start2Min) {
                return {
                    valid: false,
                    message: this.trans('pages.settings_schedule.error_messages.windows_overlap'),
                };
            }

            return { valid: true };
        },
        updateScheduleSettings() {
            let validSettings = true;

            Object.keys(this.all).forEach((key) => {
                if (!this.validate(this.validations[key], this[key])) {
                    validSettings = false;
                }
            });

            if (!validSettings) {
                showErrorMessage(
                    this.trans('pages.settings_schedule.fix_input_errors')
                );

                return;
            }

            const windowsValidation = this.validateWorkTimeWindows();
            if (!windowsValidation.valid) {
                showErrorMessage(windowsValidation.message);
                return;
            }

            settingsApi
                .updateSettings(this.all)
                .then(() => {
                    showInfoMessage(
                        this.trans('pages.settings_schedule.settings_saved')
                    );
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.settings_schedule.settings_not_saved'
                        )
                    );
                });
        },
    },
    mounted() {
        this.loading = true;
        const keys = Object.keys(this.all);
        settingsApi.fetchSettings(keys).then((response) => {
            keys.forEach((x) => {
                this[x] = response.data.data[x];
            });
            this.loading = false;
        });
    },
};
</script>
